import { Ref } from "vue";
import { Plugin, Chart, Point } from "chart.js";

interface HandlePreviewPluginOptions {
	handlePoint: Ref<Point | null>;
	active: Ref<boolean>;
}

export function useHandlePreviewPlugin(
	pluginOptions: HandlePreviewPluginOptions
): Plugin {
	return {
		id: "previewHandle",
		afterEvent(chart, args, options) {
			if (!pluginOptions.active) return;
			if (args.event.type !== "mousemove") return;
			const event = args.event.native as MouseEvent;
			if (!event) return;

			const mouseX = event.offsetX;
			const mouseY = event.offsetY;
			const xScale = chart.scales["x"];
			const yScale = chart.scales["y"];

			// Reset preview point if we're hovering over chart elements
			if (args.inChartArea && chart.getActiveElements().length > 0) {
				pluginOptions.handlePoint.value = null;
				chart.draw();
				return;
			}

			let closestPoint = null;
			let closestDistance = Infinity;

			// Get the base dataset index (it's after the positive offsets)
			const baseDatasetIndex = chart.data.datasets.findIndex(
				(dataset) => dataset.label === "Base Data"
			);

			// Check only the base dataset
			const dataset = chart.data.datasets[baseDatasetIndex];
			if (!dataset) return;

			// First check if we're near any existing points
			for (let i = 0; i < dataset.data.length; i++) {
				const point = dataset.data[i] as Point;
				const dx = mouseX - xScale.getPixelForValue(point.x);
				const dy = mouseY - yScale.getPixelForValue(point.y);
				const distanceToPoint = Math.sqrt(dx * dx + dy * dy);

				// If within hit radius, don't show preview
				// @ts-ignore Assume it will always and only use this with the drag plugin
				if (distanceToPoint <= (dataset.pointHitRadius || 10)) {
					pluginOptions.handlePoint.value = null;
					chart.draw();
					return;
				}
			}

			// Check each line segment in the dataset
			for (let i = 0; i < dataset.data.length - 1; i++) {
				const p1 = dataset.data[i] as Point;
				const p2 = dataset.data[i + 1] as Point;

				// Convert data points to pixels
				const x1 = xScale.getPixelForValue(p1.x);
				const y1 = yScale.getPixelForValue(p1.y);
				const x2 = xScale.getPixelForValue(p2.x);
				const y2 = yScale.getPixelForValue(p2.y);

				const result = distanceToLineSegment(mouseX, mouseY, x1, y1, x2, y2);

				if (result.distance < closestDistance && result.distance < 10) {
					// Calculate potential new x coordinate
					const potentialX = xScale.getValueForPixel(result.point.x);
					const isTooClose = dataset.data.some(
						(point: Point) => Math.abs(point.x - potentialX) < 0.1
					);

					if (!isTooClose) {
						closestDistance = result.distance;
						closestPoint = {
							x: potentialX,
							y: yScale.getValueForPixel(result.point.y),
						};
					}
				}
			}

			pluginOptions.handlePoint.value = closestPoint;
			chart.draw();
		},
		afterDraw(chart: Chart) {
			const previewPoint = pluginOptions.handlePoint.value;
			if (!previewPoint || !pluginOptions.active.value) return;

			const ctx = chart.ctx;
			const xScale = chart.scales["x"];
			const yScale = chart.scales["y"];

			const x = xScale.getPixelForValue(previewPoint.x);
			const y = yScale.getPixelForValue(previewPoint.y);

			// Draw the preview handle
			ctx.save();
			ctx.beginPath();
			ctx.arc(x, y, 6, 0, Math.PI * 2);
			ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
			ctx.lineWidth = 2;
			ctx.stroke();
			ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
			ctx.fill();
			ctx.restore();
		},
	};
}

function distanceToLineSegment(
	x: number,
	y: number,
	x1: number,
	y1: number,
	x2: number,
	y2: number
): { distance: number; point: Point } {
	const A = x - x1;
	const B = y - y1;
	const C = x2 - x1;
	const D = y2 - y1;

	const dot = A * C + B * D;
	const len_sq = C * C + D * D;
	let param = -1;

	if (len_sq !== 0) {
		param = dot / len_sq;
	}

	let xx: number;
	let yy: number;
	if (param < 0) {
		xx = x1;
		yy = y1;
	} else if (param > 1) {
		xx = x2;
		yy = y2;
	} else {
		xx = x1 + param * C;
		yy = y1 + param * D;
	}

	const dx = x - xx;
	const dy = y - yy;

	return {
		distance: Math.sqrt(dx * dx + dy * dy),
		point: { x: xx, y: yy },
	};
}
